import { cn } from "@/lib/utils";
import { formatNOK, formatHourlyRate } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";
import { CollapsibleCard } from "@/components/shared/CollapsibleCard";

interface KpiDashboardProps {
  totalHours: number;
  totalBudgetedHours: number;
  bfEstimated: number | null;
  bfActual: number | null;
  estTimepris: number | null;
  reellTimespris: number | null;
  /**
   * VIB-722c: når false, skjules BF-kort + timepris-kort (økonomi-data).
   * Timer-kort og budsjettavvik beholdes for alle som ser tabben.
   * Admin/superadmin overstyrer alltid via view_economics-permission.
   */
  canViewEconomics?: boolean;
}

type Status = "good" | "warn" | "bad" | "neutral";

function deltaColor(status: Status): string {
  switch (status) {
    case "good": return "text-success";
    case "warn": return "text-amber-600";
    case "bad": return "text-destructive";
    default: return "text-muted-foreground";
  }
}

function progressBarColor(pct: number): string {
  if (pct > 100) return "bg-destructive";
  if (pct >= 80) return "bg-amber-500";
  return "bg-success";
}

export function KpiDashboard({
  totalHours,
  totalBudgetedHours,
  bfEstimated,
  bfActual,
  estTimepris,
  reellTimespris,
  canViewEconomics = true,
}: KpiDashboardProps) {
  // BF statuses
  const bfEstStatus: Status = bfEstimated == null ? "neutral" : Number(bfEstimated) > 0 ? "good" : "bad";
  const bfActStatus: Status = bfActual == null ? "neutral" : Number(bfActual) > 0 ? "good" : "bad";

  // Timer
  const hoursPct = totalBudgetedHours > 0 ? Math.round((totalHours / totalBudgetedHours) * 100) : 0;
  const hoursStatus: Status =
    totalBudgetedHours === 0 ? "neutral" : hoursPct > 100 ? "bad" : hoursPct >= 80 ? "warn" : "good";

  // Timepris diff
  const timeprisDiff = estTimepris != null && reellTimespris != null ? reellTimespris - estTimepris : null;
  const timeprisStatus: Status = timeprisDiff == null ? "neutral" : timeprisDiff >= 0 ? "good" : "bad";

  // Budsjetteringsavvik: (brukt - budsjettert) / budsjettert * 100
  const budgetAvvik = totalBudgetedHours > 0
    ? Math.round(((totalHours - totalBudgetedHours) / totalBudgetedHours) * 100)
    : null;
  const avvikStatus: Status =
    budgetAvvik == null ? "neutral" : budgetAvvik <= 0 ? "good" : budgetAvvik <= 20 ? "warn" : "bad";

  return (
    <CollapsibleCard
      title="Nøkkeltall"
      icon={<SrIcon name="dashboard" className="w-5 h-5 text-primary" />}
      contentClassName="border-t-[1.5px] border-foreground grid grid-cols-2 lg:grid-cols-3 gap-[1.5px] bg-foreground"
      defaultOpen
    >
      {/* BF Estimert */}
      {canViewEconomics && (
        <div className="bg-card p-5 pb-[22px]">
          <p className="text-[11.5px] font-medium uppercase tracking-[0.08em] opacity-60">GEEK estimert</p>
          <p className={cn("font-display font-semibold text-[32px] leading-[1.05] tracking-[-0.02em] tabular-nums mt-2", deltaColor(bfEstStatus))}>
            {formatNOK(Number(bfEstimated ?? 0))}
          </p>
        </div>
      )}

      {/* BF Reell */}
      {canViewEconomics && (
        <div className="bg-card p-5 pb-[22px]">
          <p className="text-[11.5px] font-medium uppercase tracking-[0.08em] opacity-60">GEEK reell</p>
          <p className={cn("font-display font-semibold text-[32px] leading-[1.05] tracking-[-0.02em] tabular-nums mt-2", deltaColor(bfActStatus))}>
            {formatNOK(Number(bfActual ?? 0))}
          </p>
        </div>
      )}

      {/* Timer brukt */}
      <div className="bg-card p-5 pb-[22px]">
        <p className="text-[11.5px] font-medium uppercase tracking-[0.08em] opacity-60">Timer brukt</p>
        <div className="flex items-baseline gap-2 mt-2">
          <p className={cn("font-display font-semibold text-[32px] leading-[1.05] tracking-[-0.02em] tabular-nums", deltaColor(hoursStatus))}>
            {totalHours}t
          </p>
          {totalBudgetedHours > 0 && (
            <span className="text-sm text-muted-foreground tabular-nums">/ {totalBudgetedHours}t</span>
          )}
        </div>
        {totalBudgetedHours > 0 && (
          <div className="h-1.5 w-full bg-muted overflow-hidden mt-2">
            <div
              className={cn("h-full transition-all", progressBarColor(hoursPct))}
              style={{ width: `${Math.min(hoursPct, 100)}%` }}
            />
          </div>
        )}
      </div>

      {/* Est. Timepris */}
      {canViewEconomics && (
        <div className="bg-card p-5 pb-[22px]">
          <p className="text-[11.5px] font-medium uppercase tracking-[0.08em] opacity-60">Est. Timepris</p>
          <p className="font-display font-semibold text-[32px] leading-[1.05] tracking-[-0.02em] tabular-nums mt-2">
            {formatHourlyRate(estTimepris ?? 0)}
          </p>
        </div>
      )}

      {/* Live timepris */}
      {canViewEconomics && (
        <div className="bg-card p-5 pb-[22px]">
          <p className="text-[11.5px] font-medium uppercase tracking-[0.08em] opacity-60">Live timepris</p>
          <p className={cn("font-display font-semibold text-[32px] leading-[1.05] tracking-[-0.02em] tabular-nums mt-2", deltaColor(timeprisStatus))}>
            {formatHourlyRate(reellTimespris ?? 0)}
          </p>
          {timeprisDiff != null && (
            <p className={cn("text-[12.5px] tabular-nums font-medium mt-1.5", deltaColor(timeprisStatus))}>
              {timeprisDiff >= 0 ? "+" : ""}
              {formatHourlyRate(timeprisDiff)} vs estimert
            </p>
          )}
        </div>
      )}

      {/* Budsjetteringsavvik */}
      <div className="bg-card p-5 pb-[22px]">
        <p className="text-[11.5px] font-medium uppercase tracking-[0.08em] opacity-60">Budsjetteringsavvik</p>
        <p className={cn("font-display font-semibold text-[32px] leading-[1.05] tracking-[-0.02em] tabular-nums mt-2", deltaColor(avvikStatus))}>
          {budgetAvvik != null ? `${budgetAvvik >= 0 ? "+" : ""}${budgetAvvik}%` : "—"}
        </p>
        {totalBudgetedHours > 0 && (
          <p className="text-[12.5px] text-muted-foreground tabular-nums mt-1.5">
            {totalHours}t brukt av {totalBudgetedHours}t budsjettert
          </p>
        )}
      </div>
    </CollapsibleCard>
  );
}
